import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { isMobileScreen, getFlaggedColor, openInNewTab } from 'srcReact/helpers/utils'
import styles from './styles.module.scss'
import Icon from 'srcReact/components/icons/Icon'
import history, { redirectToPage } from 'srcReact/helpers/history'
import Badge from 'srcReact/components/common2/Badge'
import RSVP from 'srcReact/components/common2/RSVP'
import ViewMixrPopin from 'srcReact/components/popinsContent/ViewMixrPopin'
import config from 'config'
import {
  convertUTCToTimezone,
  getCustomFormat2,
  getTimeFormat1,
  getTimeFormat2,
  isToday,
  isTomorrow,
  getCustomFormat1_SR,
  getCustomDateFormat,
  isSameDate
} from 'srcReact/helpers/datetimeHelper'
import SuspendText from '../SuspendText'
import AnchorLink from 'srcReact/components/common2/AnchorLink'

const MixrCard = ({
  mixrId,
  groupId,
  title, //mixr name
  description,
  picture,
  name, //group name
  participants,
  startDate,
  endDate,
  isMixrOver,
  isMixrCancelled,
  color,
  timezone,
  timezoneFullName,
  utcOffset,
  location,
  url,
  isRecurrence,
  parentMixrId,
  isVisibility,
  attendance,
  userAttendance,
  layout,
  className,
  isViewModal,
  organizer,
  infiniteScrollRef,
  isInvited,
  isCtaButton,
  isFutureMixrEmpty,
  menuPortalTarget = false,
  withGroupDetails = false,
  groupAttendingStatus,
  mixrOrganizer = '',
  showRecurrentIcon = true,
  showPrivateIcon = true,
  flaggedList,
  isGroupSuspended,
  isSuspended,
  mixrRole,
  groupRole,
  withoutGroupApproval,
  isJoinCallButton,
  joinLink,
  groupType,
  recurrenceMixrAttending,
  origin = ''
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    common: { isNative },
    groupHome: { group },
    myhome: {
      invitations: { list, loading }
    }
  } = useSelector((state) => state)
  const [isMobile, setIsMobile] = useState(isMobileScreen())
  const [showViewModal, setShowViewModal] = useState(false)
  const [disableCard, setDisableCard] = useState(false)
  const [dateFontSmall, setDateFontSmall] = useState(false)

  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange)
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange)
    }
  }, [])

  useEffect(() => {
    if (startDate && endDate) {
      const convertedStartDate = convertUTCToTimezone(startDate, utcOffset)
      const convertedEndDate = convertUTCToTimezone(endDate, utcOffset)
      const isMultipleDayMixr = !isSameDate(convertedEndDate, convertedStartDate)
      if (isMultipleDayMixr) {
        if (
          (isToday(convertedStartDate.toDate()) || isTomorrow(convertedStartDate.toDate())) &&
          origin === 'carousel'
        ) {
          setDateFontSmall(true)
        }
      }
    }
  }, [startDate, endDate])

  useEffect(() => {
    if (isGroupSuspended) {
      if (isInGroupStaffRole()) {
        setDisableCard(false)
      } else {
        setDisableCard(true)
      }
    } else if (isSuspended && !isInMixrStaffRole()) {
      setDisableCard(true)
    }
  }, [isSuspended, isGroupSuspended])

  const handleWindowSizeChange = () => {
    setIsMobile(isMobileScreen())
  }

  const resetScroll = async () => {
    setShowViewModal(false)
  }

  const isInMixrStaffRole = () => {
    return mixrRole === 'organizer' || mixrRole === 'co-organizer'
  }
  const isInGroupStaffRole = () => {
    return groupRole === 'organizer' || groupRole === 'co-organizer'
  }

  const handleClick = () => {
    if (isGroupSuspended) {
      if (isInGroupStaffRole()) {
        redirectToPage(`/0/home/group/${groupId}`, isNative, true)
      }
      return
    }
    if (isSuspended && !isInMixrStaffRole()) {
      return
    }
    if (isViewModal) {
      setShowViewModal(!showViewModal)
      return
    }
    //nativeapp
    if (parentMixrId) {
      redirectToPage(`/0/home/event/${parentMixrId}`, isNative, true)
      return
    }
    redirectToPage(`/0/home/event/${mixrId}`, isNative, true)
  }

  const handleViewMixrPopin = async () => {
    await resetScroll()
    //nativeapp
    if (isNative) {
      window.location = config.webUrl + `/0/home/event/${mixrId}?backNavigation=true`
      return
    }
    history.push(`/0/home/event/${mixrId}`, { backNavigation: true })
  }

  const handleNavigateToGroup = () => {
    //nativeapp
    if (isNative) {
      window.location = config.webUrl + `/0/home/group/${groupId}?backNavigation=true`
      return
    }
    history.push(`/0/home/group/${groupId}`, { backNavigation: true })
  }

  const isFlagged = flaggedList.length >= 1 && flaggedList.find((f) => f.id_mixr === mixrId)

  const renderViewMixrPopin = (
    <ViewMixrPopin
      mixrId={mixrId}
      title={title}
      picture={picture}
      startDate={startDate}
      endDate={endDate}
      utcOffset={utcOffset}
      timezone={timezone}
      isMixrOver={isMixrOver}
      description={description}
      color={color}
      isRecurrence={isRecurrence}
      attendance={attendance}
      isVisibility={isVisibility}
      userAttendance={userAttendance}
      location={location}
      isInvited={isInvited}
      showViewMixr={showViewModal}
      closeViewMixr={() => setShowViewModal(false)}
      handleClick={handleViewMixrPopin}
      group={{ id: groupId, name: name, role: groupRole, attending: groupAttendingStatus }}
      mixrRole={mixrRole}
      withoutGroupApproval={withoutGroupApproval}
    />
  )

  const renderMixrActions = () => {
    if (isMixrOver) {
      return <div className={`${styles.button} ${styles.past}`}>{t('myhome.mymixrs.over')}</div>
    }
    if (isMixrCancelled) {
      return <div className={`${styles.button} ${styles.cancelled}`}>{t('myhome.mymixrs.cancelled')}</div>
    } else if (isFutureMixrEmpty) {
      return (
        <AnchorLink className={`${styles.button} ${styles.notFound}`} path={`/0/discover`}>
          {t('myhome.mymixrs.notFound.button')}
        </AnchorLink>
      )
    } else if (isCtaButton) {
      return (
        <AnchorLink className={`${styles.button} ${styles.notFound}`} path={`/0/create-mixr`}>
          {t('nav.create_mixr')}
        </AnchorLink>
      )
    } else if (isViewModal) {
      return (
        <AnchorLink
          className={`${styles.button} ${styles.future}`}
          handleClick={() => setShowViewModal(!showViewModal)}>
          {t('myhome.mymixrs.view')}
        </AnchorLink>
      )
    } else if (isJoinCallButton && joinLink) {
      return (
        <AnchorLink
          className={styles.joinCallBtn}
          icon={{
            name: 'arrow-right',
            position: 'end',
            alt: '',
            size: 'large',
            styleModifier: styles.arrowRight
          }}
          handleClick={() => openInNewTab(joinLink)}>
          {t('mixrHome.joinCall')}
        </AnchorLink>
      )
    } else if (!isSuspended) {
      return (
        <RSVP
          mixrId={mixrId}
          groupId={groupId}
          groupName={name}
          groupRole={groupRole}
          selectedUserAttending={userAttendance}
          recurrenceMixrAttending={recurrenceMixrAttending}
          groupAttending={groupAttendingStatus}
          mixrAttendingType={attendance}
          menuPortalTarget={menuPortalTarget}
          withoutGroupAproval={withoutGroupApproval}
          isInvite={isInvited}
          className={styles.rsvpButton}
          isRecurrence={isRecurrence}
        />
      )
    }
  }

  if (isFutureMixrEmpty || isCtaButton) {
    return (
      <div className={styles.mixrCardWrapper}>
        <div className={styles.verticalCard}>
          <div className={styles.cardHeader} />
          <div className={`${styles.cardBody} ${isMobile && !withGroupDetails && styles.cardBodyCenter}`}>
            <div className={styles.notFoundTitle}>
              {isCtaButton ? t('global.ctaMixrTitle') : t('myhome.mymixrs.notFound.title')}
            </div>
          </div>
          <div className={`${styles.cardFooter} ${isMobile && !withGroupDetails && styles.cardFooterColumn}`}>
            {renderMixrActions()}
          </div>
        </div>
      </div>
    )
  }

  if (!isMobile && layout === 'horizontal') {
    return (
      <article className={`${styles.mixrCardWrapper} ${className}`}>
        <div className={`${styles.horizontalCard} ${disableCard && styles.disabledCard}`} ref={infiniteScrollRef}>
          {isSuspended && (
            <SuspendText
              className={styles.suspendedMixrHorz}
              text={t('moderationReport.suspend.objSuspended', { object: 'mixr' })}
            />
          )}
          {isVisibility && (
            <div className={styles.privacy} title={t('groupHome.header.private')}>
              <Icon name='mask' styleModifier={styles.maskIcon} size='large' />
            </div>
          )}

          <div className={`${styles.cardHeader} ${styles[color]} ${styles.horizontal}`} onClick={handleClick}>
            {isRecurrence && <Icon name='recurring-mixrs' styleModifier={styles.recurringIcon} />}

            <span
              className={styles.dateVertical}
              dangerouslySetInnerHTML={{ __html: getCustomFormat2(startDate, endDate, utcOffset) }}
            />
          </div>

          <div className={styles.cardBody} onClick={handleClick}>
            {picture ? (
              <img src={picture} alt={title} className={styles.image} />
            ) : (
              <img
                src={require('../../../assets/images/background-default-mixr.jpg')}
                alt={title}
                className={styles.image}
              />
            )}

            <div>
              <div className={styles.info}>
                {`${convertUTCToTimezone(startDate, utcOffset, getTimeFormat1())} - ${convertUTCToTimezone(
                  endDate,
                  utcOffset,
                  getTimeFormat1()
                )}`}{' '}
                <span className={styles.timezone}>{timezone}</span>
                {mixrOrganizer && ' | ' + mixrOrganizer}
                {attendance && ' | ' + t(`myhome.mymixrs.attendingType.${attendance}`)}
              </div>
              <div className={styles.title}>{title}</div>
              <div className={styles.description} dangerouslySetInnerHTML={{ __html: description }} />
            </div>
          </div>

          <div className={styles.cardFooter}>
            {isMixrOver &&
              userAttendance &&
              userAttendance !== 'unknown' &&
              userAttendance !== 'none' &&
              userAttendance !== 'can_t_go' && <span className={styles.attended}>{t('myhome.mymixrs.attended')}</span>}

            {isMixrOver ? (
              <button className={`${styles.button} ${styles.past}`}>{t('myhome.mymixrs.over')}</button>
            ) : isViewModal ? (
              <button className={`${styles.button} ${styles.future}`} onClick={() => setShowViewModal(!showViewModal)}>
                {t('myhome.mymixrs.view')}
              </button>
            ) : !isSuspended ? (
              <RSVP
                mixrId={mixrId}
                groupId={groupId}
                groupName={name}
                groupRole={groupRole}
                selectedUserAttending={userAttendance}
                recurrenceMixrAttending={recurrenceMixrAttending}
                groupAttending={groupAttendingStatus}
                mixrAttendingType={attendance}
                menuPortalTarget={menuPortalTarget}
                withoutGroupAproval={withoutGroupApproval}
                isInvite={isInvited}
                className={styles.rsvpButton}
                isRecurrence={isRecurrence}
              />
            ) : null}
            <div className={styles.members}>
              <Icon styleModifier={styles.memberIcon} name='groups24' />
              <span className={styles.participants}>{participants}</span>
            </div>
          </div>
        </div>
        {isViewModal && renderViewMixrPopin}
      </article>
    )
  }

  return (
    <div className={`${styles.mixrCardWrapper} ${className}`}>
      <article
        aria-description={
          isMixrOver
            ? t('global.mixrCardDescOver', {
                mixrName: title,
                date: getCustomFormat1_SR(startDate, utcOffset),
                groupName: name
              })
            : t('global.mixrCardDesc', {
                mixrName: title,
                date: getCustomFormat1_SR(startDate, utcOffset),
                startTime: convertUTCToTimezone(startDate, utcOffset, getTimeFormat2()),
                endTime: `${convertUTCToTimezone(endDate, utcOffset, getTimeFormat2())} ${timezoneFullName}`,
                groupName: name
              })
        }
        tabIndex={0}
        ref={infiniteScrollRef}
        className={`${styles.verticalCard} ${userAttendance === null && styles.verticalCardNotGoing} ${
          disableCard && styles.disabledCard
        } `}>
        <div className={`${styles.cardHeader} ${styles.mixrHeader} ${styles[color]}`} onClick={handleClick}>
          {isRecurrence && showRecurrentIcon && (
            <Icon name='recurring-mixrs' styleModifier={styles.recurringIcon} size='large24' />
          )}

          <div
            className={`${styles.date} ${dateFontSmall ? styles.dateFontSmall : ''}`}
            dangerouslySetInnerHTML={{
              __html: getCustomDateFormat(
                convertUTCToTimezone(startDate, utcOffset),
                convertUTCToTimezone(endDate, utcOffset),
                utcOffset,
                t
              )
            }}
          />
          <div className={styles.time}>
            {isMobile
              ? `${convertUTCToTimezone(startDate, utcOffset, getTimeFormat2())}-${convertUTCToTimezone(
                  endDate,
                  utcOffset,
                  getTimeFormat2()
                )}`
              : `${convertUTCToTimezone(startDate, utcOffset, getTimeFormat1())} - ${convertUTCToTimezone(
                  endDate,
                  utcOffset,
                  getTimeFormat1()
                )}`}{' '}
            <span>{timezone}</span>
          </div>
          {isVisibility && showPrivateIcon && <Icon name='mask' styleModifier={styles.maskIcon} />}
          {groupType === 'expertise' && !isMobile && (
            <Icon
              name='star-solid'
              styleModifier={`${styles.expertiseIcon} ${
                isVisibility && showPrivateIcon && styles.expertiseIconShifted
              }`}
              size='large'
            />
          )}
        </div>
        <div
          className={`${styles.cardBody} ${isMobile && !withGroupDetails && styles.cardBodyCenter}`}
          onClick={handleClick}>
          {!isMobile || withGroupDetails ? (
            picture ? (
              <img src={picture} alt={title} className={styles.image} />
            ) : (
              <img
                src={require('../../../assets/images/background-default-mixr.jpg')}
                alt={title}
                className={styles.image}
              />
            )
          ) : null}

          <div className={styles.title}>{title}</div>
        </div>
        <div className={`${styles.cardFooter} ${isMobile && !withGroupDetails && styles.cardFooterColumn}`}>
          <div className={styles.mixrDetails}>
            {(!isMobile || withGroupDetails) && (
              <div role='button' className={styles.name} onClick={handleNavigateToGroup}>
                {name}
              </div>
            )}
            <span className={styles.participants}>
              {participants} {t('myhome.mymixrs.participants')}
            </span>
          </div>
          {renderMixrActions()}
        </div>
        {isSuspended ? (
          <SuspendText
            className={styles.suspendedMixr}
            text={
              isMobile || isNative
                ? t('moderationReport.suspend.suspended')
                : t('moderationReport.suspend.objSuspended', { object: 'mixr' })
            }
          />
        ) : (
          isFlagged &&
          isFlagged.flag_count >= 1 && (
            <div className={styles.flagBadge}>
              <Badge
                size='large'
                color={getFlaggedColor(isFlagged.flag_count)}
                icon='flag-icon'
                text={isFlagged.flag_count}
              />
            </div>
          )
        )}
      </article>
      {isViewModal && renderViewMixrPopin}
    </div>
  )
}

MixrCard.propTypes = {
  mixrId: PropTypes.number,
  groupId: PropTypes.number,
  title: PropTypes.string,
  description: PropTypes.string,
  picture: PropTypes.string,
  name: PropTypes.string,
  participants: PropTypes.number,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  isMixrOver: PropTypes.bool,
  isMixrCancelled: PropTypes.bool,
  isFutureMixrEmpty: PropTypes.bool,
  isFutureMixrEmpty: PropTypes.bool,
  color: PropTypes.string,
  timezone: PropTypes.string,
  utcOffset: PropTypes.string,
  location: PropTypes.string,
  url: PropTypes.string,
  isRecurrence: PropTypes.bool,
  isVisibility: PropTypes.bool,
  attendance: PropTypes.string,
  userAttendance: PropTypes.string,
  layout: PropTypes.oneOf(['horizontal', 'vertical']),
  className: PropTypes.string,
  isViewModal: PropTypes.bool,
  isInvited: PropTypes.bool,
  infiniteScrollRef: PropTypes.func,
  flaggedList: PropTypes.array,
  organizer: PropTypes.shape({
    name: PropTypes.string,
    picture: PropTypes.string
  }),
  isJoinCallButton: PropTypes.bool,
  joinLink: PropTypes.string
}

MixrCard.defaultProps = {
  participants: 0,
  isViewModal: false,
  isInvited: false,
  isMixrOver: false,
  isRecurrence: false,
  isVisibility: false,
  isFutureMixrEmpty: false,
  isFutureMixrEmpty: false,
  layout: 'vertical',
  flaggedList: [],
  organizer: {
    name: '',
    picture: ''
  },
  isJoinCallButton: false,
  joinLink: ''
}

export default MixrCard
